import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";

import { AuthProvider } from "@/context/AuthContext";
import { SiteContentProvider } from "@/context/SiteContentContext";
import Layout from "@/components/layout/Layout";
import AdminRoute from "@/components/AdminRoute";

import Home from "@/pages/Home";
import Technologies from "@/pages/Technologies";
import Platforms from "@/pages/Platforms";
import Applications from "@/pages/Applications";
import CaseStudies from "@/pages/CaseStudies";
import CaseStudyDetail from "@/pages/CaseStudyDetail";
import Services from "@/pages/Services";
import Insights from "@/pages/Insights";
import InsightDetail from "@/pages/InsightDetail";
import About from "@/pages/About";
import Contact from "@/pages/Contact";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import PostEditor from "@/pages/admin/PostEditor";
import ContentEditor from "@/pages/admin/ContentEditor";

function ScrollToTop() {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        if (hash) {
            // Wait a tick for the destination page to mount, then scroll to the anchor.
            const id = hash.slice(1);
            const tryScroll = () => {
                const el = document.getElementById(id);
                if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                    return true;
                }
                return false;
            };
            if (!tryScroll()) setTimeout(tryScroll, 80);
        } else {
            window.scrollTo({ top: 0, behavior: "instant" });
        }
    }, [pathname, hash]);
    return null;
}

function PublicSite({ children }) {
    return <Layout>{children}</Layout>;
}

function App() {
    return (
        <div className="App">
            <HelmetProvider>
            <BrowserRouter>
                <AuthProvider>
                    <SiteContentProvider>
                    <ScrollToTop />
                    <Toaster
                        theme="dark"
                        position="top-right"
                        toastOptions={{
                            style: {
                                background: "#0e0e1c",
                                color: "#fff",
                                border: "1px solid rgba(255,255,255,0.12)",
                                fontFamily: "JetBrains Mono, monospace",
                                fontSize: "12px",
                                letterSpacing: "0.06em",
                                borderRadius: 2,
                            },
                        }}
                    />
                    <Routes>
                        <Route path="/" element={<PublicSite><Home /></PublicSite>} />
                        <Route path="/technologies" element={<PublicSite><Technologies /></PublicSite>} />
                        <Route path="/platforms" element={<PublicSite><Platforms /></PublicSite>} />
                        <Route path="/applications" element={<PublicSite><Applications /></PublicSite>} />
                        <Route path="/case-studies" element={<PublicSite><CaseStudies /></PublicSite>} />
                        <Route path="/case-studies/:slug" element={<PublicSite><CaseStudyDetail /></PublicSite>} />
                        <Route path="/services" element={<PublicSite><Services /></PublicSite>} />
                        <Route path="/insights" element={<PublicSite><Insights /></PublicSite>} />
                        <Route path="/insights/:slug" element={<PublicSite><InsightDetail /></PublicSite>} />
                        <Route path="/about" element={<PublicSite><About /></PublicSite>} />
                        <Route path="/contact" element={<PublicSite><Contact /></PublicSite>} />

                        <Route path="/admin/login" element={<AdminLogin />} />
                        <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                        <Route path="/admin/content" element={<AdminRoute><ContentEditor /></AdminRoute>} />
                        <Route path="/admin/posts/new" element={<AdminRoute><PostEditor /></AdminRoute>} />
                        <Route path="/admin/posts/:id/edit" element={<AdminRoute><PostEditor /></AdminRoute>} />
                    </Routes>
                    </SiteContentProvider>
                </AuthProvider>
            </BrowserRouter>
            </HelmetProvider>
        </div>
    );
}

export default App;
